
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, RefreshCw } from "lucide-react";
import { getCompanyDetailsByPartnerId } from "../services/PartnerService";
import "./admin.css";

const getBody = (response) =>
  response?.data?.data ?? response?.data;

const LABELS = {
  companyLegalName: "Legal Company Name",
  companyName: "Company Name",
  companyDisplayName: "Display Name",
  tradeName: "Trade Name",
  brandName: "Brand Name",
  companyType: "Company Type",
  registrationNumber: "Registration Number",
  cin: "CIN",
  gst: "GST Number",
  gstNumber: "GST Number",
  gstin: "GSTIN",
  pan: "PAN Number",
  panNumber: "PAN Number",
  tan: "TAN Number",
  website: "Website",
  address: "Address",
  registeredAddress: "Registered Address",
  city: "City",
  state: "State",
  pincode: "Pincode",
  authorizedPerson: "Authorized Person",
  authorisedPerson: "Authorised Person",
  contactPerson: "Contact Person",
  name: "Name",
  designation: "Designation",
  email: "Email",
  emailId: "Email",
  mobile: "Mobile",
  phone: "Phone",
  contactNumber: "Contact Number",
  bankName: "Bank Name",
  accountHolderName: "Account Holder Name",
  accountNumber: "Account Number",
  ifsc: "IFSC Code",
  ifscCode: "IFSC Code",
  branch: "Branch",
  remark: "Verification Remark",
  verificationStatus: "Verification Status",
  status: "Company Status",
};

const GROUPS = [
  {
    title: "Company Information",
    keys: [
      "companyLegalName",
      "companyName",
      "companyDisplayName",
      "tradeName",
      "brandName",
      "companyType",
      "registrationNumber",
      "cin",
      "website",
    ],
  },
  {
    title: "Tax Details",
    keys: [
      "gst",
      "gstNumber",
      "gstin",
      "pan",
      "panNumber",
      "tan",
      "tanNumber",
    ],
  },
  {
    title: "Registered Address",
    keys: [
      "address",
      "registeredAddress",
      "city",
      "state",
      "pincode",
    ],
  },
  {
    title: "Authorized Person",
    keys: [
      "authorizedPerson",
      "authorisedPerson",
      "contactPerson",
      "name",
      "designation",
      "email",
      "emailId",
      "mobile",
      "phone",
      "contactNumber",
    ],
  },
  {
    title: "Bank Details",
    keys: [
      "bankName",
      "accountHolderName",
      "accountNumber",
      "ifsc",
      "ifscCode",
      "branch",
    ],
  },
];

const hiddenKeys = new Set([
  "id",
  "partnerId",
  "partner",
  "createdAt",
  "updatedAt",
  "password",
  "token",
]);

const prettify = (key) =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

function CompanyDetailsPage() {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCompany = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getCompanyDetailsByPartnerId(partnerId);
      const body = getBody(response);

      if (!body || Array.isArray(body) || typeof body !== "object") {
        throw new Error("The company details response is invalid.");
      }

      setCompany(body);
    } catch (err) {
      console.error("COMPANY DETAILS ERROR:", err);
      setError(
        err.response?.status === 404
          ? "No company details were found for this partner."
          : err.response?.data?.message ||
              err.message ||
              "Unable to load company details."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
  }, [partnerId]);

  const renderedKeys = new Set();

  const renderField = (key) => {
    if (
      renderedKeys.has(key) ||
      hiddenKeys.has(key) ||
      company?.[key] === undefined
    ) {
      return null;
    }

    renderedKeys.add(key);

    const value = company[key];

    if (
      value &&
      typeof value === "string" &&
      /^https?:\/\//i.test(value)
    ) {
      return (
        <div className="company-detail-field" key={key}>
          <span>{LABELS[key] || prettify(key)}</span>
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        </div>
      );
    }

    return (
      <div className="company-detail-field" key={key}>
        <span>{LABELS[key] || prettify(key)}</span>
        <strong>{displayValue(value)}</strong>
      </div>
    );
  };

  return (
    <div className="partner-page">
      <div className="partner-header">
        <div className="partner-header-left">
          <div className="partner-title-icon">
            <Building2 size={20} />
          </div>

          <div>
            <h1>Company Details</h1>
            <p>Partner ID: {partnerId}</p>
          </div>
        </div>

        <div className="partner-header-actions">
          <button
            type="button"
            className="partner-clear-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <button
            type="button"
            className="partner-export-btn"
            onClick={loadCompany}
            disabled={loading}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="partner-table-card partner-empty">
          Loading company details...
        </div>
      ) : error ? (
        <div className="partner-table-card">
          <div className="partner-empty">{error}</div>
        </div>
      ) : (
        <>
          <div className="partner-table-card company-detail-card">
            <div className="partner-table-header">
              <div>
                <h2>
                  {company.companyLegalName ||
                    company.companyName ||
                    company.companyDisplayName ||
                    "Company Information"}
                </h2>
                <span>
                  Company status: {company.status || "-"} | Verification:{" "}
                  {company.verificationStatus || "-"}
                </span>
              </div>
            </div>

            <div className="company-detail-sections">
              {GROUPS.map((group) => {
                const fields = group.keys
                  .map(renderField)
                  .filter(Boolean);

                return fields.length ? (
                  <section
                    className="company-detail-section"
                    key={group.title}
                  >
                    <h3>{group.title}</h3>
                    <div className="company-detail-grid">
                      {fields}
                    </div>
                  </section>
                ) : null;
              })}

              {(() => {
                const otherFields = Object.keys(company)
                  .filter(
                    (key) =>
                      !renderedKeys.has(key) &&
                      !hiddenKeys.has(key) &&
                      !/password|token/i.test(key) &&
                      !/document|file|certificate|upload|proof|image|photo|logo|path|url/i.test(
                        key
                      )
                  )
                  .map(renderField)
                  .filter(Boolean);

                return otherFields.length ? (
                  <section className="company-detail-section">
                    <h3>Other Details</h3>
                    <div className="company-detail-grid">
                      {otherFields}
                    </div>
                  </section>
                ) : null;
              })()}
            </div>
          </div>

          <div className="partner-header-actions page-bottom-actions">
            <button
              type="button"
              className="partner-export-btn"
              onClick={() =>
                navigate(`/admin/subscription-details/${partnerId}`)
              }
            >
              View Subscription Details
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CompanyDetailsPage;
